import { ENCODING_FORMATS } from './constants.js'
import { sign, verify } from './crypto.js'
import type { WorkerRequest } from './types.js'
import { bytesToHex, hexToBytes, sha256, textDecoder, textEncoder } from './utils.js'

/**
 * Packs a payload into a signed WorkerRequest
 */
export const packWorkerRequest = ({
  publicKey,
  privKey,
  payload,
}: {
  publicKey: Uint8Array
  privKey: Uint8Array
  payload: unknown
}): WorkerRequest => {
  const payloadBytes = textEncoder.encode(JSON.stringify(payload))
  const hash = sha256(payloadBytes)
  const signature = sign(privKey, hash)

  return {
    payload: bytesToHex(payloadBytes),
    public_key: bytesToHex(publicKey),
    signature: bytesToHex(signature),
    encoding: ENCODING_FORMATS.HEX,
  }
}

/**
 * Unpacks and verifies a WorkerRequest, returning the payload
 */
export const unpackWorkerRequestPayload = (request: WorkerRequest): unknown => {
  verifyWorkerRequestSignature(request)
  const payloadBytes = hexToBytes(request.payload)
  return JSON.parse(textDecoder.decode(payloadBytes))
}

/**
 * Verifies the Ed25519 signature of a WorkerRequest
 */
export const verifyWorkerRequestSignature = ({
  public_key,
  payload,
  signature,
}: WorkerRequest): void => {
  const hash = sha256(hexToBytes(payload))
  const isValid = verify(hexToBytes(public_key), hash, hexToBytes(signature))

  if (!isValid) {
    throw new Error('signature is not valid')
  }
}

/**
 * Validates that a WorkerRequest has all required fields
 */
export const validateWorkerRequest = (request: WorkerRequest): void => {
  if (!request.public_key) {
    throw new Error('public_key is required')
  }

  if (!request.signature) {
    throw new Error('signature is required')
  }

  if (!request.payload) {
    throw new Error('payload is required')
  }
}

/**
 * Fully validates a WorkerRequest (fields + signature)
 */
export const validateAndVerifyWorkerRequest = (request: WorkerRequest): void => {
  validateWorkerRequest(request)
  verifyWorkerRequestSignature(request)
}

// Legacy export (deprecated, use validateWorkerRequest)
export const checkWorkerRequest = validateWorkerRequest
